import type { MarketIndicator } from "@/types/market-status"

type Row = Record<string, string | number>

function sortByDate(rows: Row[]): Row[] {
  return rows.sort((a, b) => String(a.date).localeCompare(String(b.date)))
}

/** 지표를 market(kospi/kosdaq 등) 기준으로 나눠, 날짜별 { date, [seriesKey]: value } 배열로 피벗 */
export function pivotByMarket(
  rows: MarketIndicator[],
  seriesKeyByMarket: Record<string, string>
): Row[] {
  const map = new Map<string, Row>()

  for (const r of rows) {
    const seriesKey = r.market ? seriesKeyByMarket[r.market] : undefined
    if (!seriesKey) continue

    const date = r.indicatorDate.slice(5)
    const entry = map.get(date) ?? { date }
    entry[seriesKey] = r.value
    map.set(date, entry)
  }

  return sortByDate([...map.values()])
}

export interface InvestorTrendRow {
  date: string
  individual: number
  foreign: number
  institution: number
}

/** 투자자매매동향(개인/외국인/기관) 지표를 날짜별 배열로 피벗. value=개인, extra.foreign/institution */
export function pivotInvestorTrend(rows: MarketIndicator[]): InvestorTrendRow[] {
  return rows
    .map((r) => ({
      date: r.indicatorDate.slice(5),
      individual: r.value,
      foreign: r.extra?.foreign ?? 0,
      institution: r.extra?.institution ?? 0,
    }))
    .sort((a, b) => a.date.localeCompare(b.date))
}

/** 단일 계열(신용잔고, 대차잔고 등) 지표를 날짜별 { date, value } 배열로 변환 */
export function pivotSingleSeries(rows: MarketIndicator[]): Row[] {
  return sortByDate(rows.map((r) => ({ date: r.indicatorDate.slice(5), value: r.value })))
}

/** 코스피/코스닥 거래대금 비중(%) 계산 — kospiKey/kosdaqKey는 pivotByMarket 결과의 필드명 */
export function computeRatio(rows: Row[], kospiKey: string, kosdaqKey: string): Row[] {
  return rows.map((r) => {
    const kospi = Number(r[kospiKey] ?? 0)
    const kosdaq = Number(r[kosdaqKey] ?? 0)
    const total = kospi + kosdaq
    return {
      date: r.date,
      kospiRatio: total > 0 ? Number(((kospi / total) * 100).toFixed(2)) : 0,
      kosdaqRatio: total > 0 ? Number(((kosdaq / total) * 100).toFixed(2)) : 0,
    }
  })
}
